import {
  cloneState,
  type FrameworkId,
  type RelationType,
  type SchemaField,
  type SyncrelChatMessage,
  type SyncrelEntity,
  type SyncrelRelation,
  type SyncrelState,
} from "./types";
import { migrateLegacyFieldReferences } from "./state-migrations";
import { createWorkspaceShareKeys } from "./share";

export const WORKSPACE_FILE_KIND = "syncrel.workspace";
const WORKSPACE_FILE_VERSION = 1;

export interface WorkspaceExportFile {
  kind: typeof WORKSPACE_FILE_KIND;
  version: typeof WORKSPACE_FILE_VERSION;
  exportedAt: number;
  sourceRoom: string;
  sourceWorkspaceName: string;
  state: SyncrelState;
}

export const exportWorkspaceFile = (
  state: SyncrelState,
  exportedAt = Date.now(),
): string => JSON.stringify({
  kind: WORKSPACE_FILE_KIND,
  version: WORKSPACE_FILE_VERSION,
  exportedAt,
  sourceRoom: state.workspace.room,
  sourceWorkspaceName: state.workspace.name,
  state: cloneState(state),
}, null, 2);

export const parseWorkspaceFile = (raw: string): WorkspaceExportFile => {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    throw new Error("Invalid Syncrel workspace file");
  }

  if (isWorkspaceExportFileLike(parsed)) {
    return {
      kind: WORKSPACE_FILE_KIND,
      version: WORKSPACE_FILE_VERSION,
      exportedAt: Number.isFinite(parsed.exportedAt) ? parsed.exportedAt : Date.now(),
      sourceRoom: parsed.sourceRoom || parsed.state.workspace.room,
      sourceWorkspaceName: parsed.sourceWorkspaceName || parsed.state.workspace.name,
      state: normalizeState(parsed.state),
    };
  }

  if (isSyncrelStateLike(parsed)) {
    const state = normalizeState(parsed);
    return {
      kind: WORKSPACE_FILE_KIND,
      version: WORKSPACE_FILE_VERSION,
      exportedAt: Date.now(),
      sourceRoom: state.workspace.room,
      sourceWorkspaceName: state.workspace.name,
      state,
    };
  }

  throw new Error("Invalid Syncrel workspace file");
};

export const appendWorkspaceImport = (
  current: SyncrelState,
  imported: SyncrelState,
  now = Date.now(),
): SyncrelState => {
  const next = normalizeState(current);
  const incoming = normalizeState(imported);
  const suffix = `import-${now.toString(36)}`;

  const fieldKeyMap = mergeSchemaFields(next, incoming, suffix);
  const relationTypeKeyMap = mergeRelationTypes(next, incoming, suffix);
  const entityIdMap = appendEntities(next, incoming.entities, fieldKeyMap, suffix, now);

  appendRelations(next, incoming.relations, entityIdMap, relationTypeKeyMap, suffix);
  appendChat(next, incoming.chat ?? [], suffix);
  next.workspace.enabledFrameworks = mergedFrameworks(next, incoming);
  next.workspace.updatedAt = now;
  return next;
};

export const importedWorkspaceRoom = (
  imported: SyncrelState,
  existingRooms: string[],
  now = Date.now(),
): string => {
  const base = roomSlug(imported.workspace.room || imported.workspace.name || "workspace");
  const suffix = `import-${now.toString(36)}`;
  const seed = `${base}-${suffix}`;
  const used = new Set(existingRooms);
  let candidate = fitRoomName(seed);
  let index = 2;
  while (used.has(candidate)) {
    const tail = `-${index}`;
    candidate = `${fitRoomName(seed, tail.length)}${tail}`;
    index += 1;
  }
  return candidate;
};

export const prepareImportedWorkspaceState = (
  imported: SyncrelState,
  room: string,
  now = Date.now(),
): SyncrelState => {
  const next = normalizeState(imported);
  next.workspace = {
    ...next.workspace,
    id: `workspace-${room}`,
    room,
    share: createWorkspaceShareKeys(),
    updatedAt: now,
  };
  const entityIds = new Set(next.entities.map((entity) => entity.id));
  const relationIds = new Set(next.relations.map((relation) => relation.id));
  if (next.workspace.selectedEntityId && !entityIds.has(next.workspace.selectedEntityId)) {
    next.workspace.selectedEntityId = next.entities[0]?.id ?? null;
  }
  if (next.workspace.selectedRelationId && !relationIds.has(next.workspace.selectedRelationId)) {
    next.workspace.selectedRelationId = null;
  }
  return next;
};

export const workspaceFileName = (state: SyncrelState, exportedAt = Date.now()): string => (
  `${roomSlug(state.workspace.room || state.workspace.name)}-${new Date(exportedAt).toISOString().slice(0, 10)}.syncrel.json`
);

const normalizeState = (state: SyncrelState): SyncrelState => (
  migrateLegacyFieldReferences(cloneState(state))
);

const isWorkspaceExportFileLike = (value: unknown): value is WorkspaceExportFile => {
  if (!value || typeof value !== "object") return false;
  const file = value as Partial<WorkspaceExportFile>;
  return (
    file.kind === WORKSPACE_FILE_KIND &&
    file.version === WORKSPACE_FILE_VERSION &&
    isSyncrelStateLike(file.state)
  );
};

const isSyncrelStateLike = (value: unknown): value is SyncrelState => {
  if (!value || typeof value !== "object") return false;
  const state = value as Partial<SyncrelState>;
  return (
    state.version === 1 &&
    Boolean(state.workspace) &&
    typeof state.workspace?.room === "string" &&
    typeof state.workspace?.name === "string" &&
    Array.isArray(state.entities) &&
    Array.isArray(state.relations) &&
    Boolean(state.schema) &&
    Array.isArray(state.schema?.fields) &&
    Array.isArray(state.schema?.relationTypes)
  );
};

const mergeSchemaFields = (
  next: SyncrelState,
  incoming: SyncrelState,
  suffix: string,
): Map<string, string> => {
  const keyMap = new Map<string, string>();
  const used = new Set(next.schema.fields.map((field) => field.key));
  const existing = new Map(next.schema.fields.map((field) => [field.key, field]));

  for (const field of incoming.schema.fields) {
    const current = existing.get(field.key);
    if (!current) {
      next.schema.fields.push(cloneSchemaField(field));
      used.add(field.key);
      existing.set(field.key, cloneSchemaField(field));
      keyMap.set(field.key, field.key);
      continue;
    }
    if (schemaFieldsMatch(current, field)) {
      keyMap.set(field.key, field.key);
      continue;
    }
    const key = uniqueWithSuffix(field.key, used, suffix);
    const cloned = { ...cloneSchemaField(field), key, label: `${field.label} import` };
    next.schema.fields.push(cloned);
    used.add(key);
    existing.set(key, cloned);
    keyMap.set(field.key, key);
  }

  return keyMap;
};

const mergeRelationTypes = (
  next: SyncrelState,
  incoming: SyncrelState,
  suffix: string,
): Map<string, string> => {
  const keyMap = new Map<string, string>();
  const used = new Set(next.schema.relationTypes.map((type) => type.key));
  const existing = new Map(next.schema.relationTypes.map((type) => [type.key, type]));

  for (const relationType of incoming.schema.relationTypes) {
    const current = existing.get(relationType.key);
    if (!current) {
      next.schema.relationTypes.push({ ...relationType });
      used.add(relationType.key);
      existing.set(relationType.key, { ...relationType });
      keyMap.set(relationType.key, relationType.key);
      continue;
    }
    if (relationTypesMatch(current, relationType)) {
      keyMap.set(relationType.key, relationType.key);
      continue;
    }
    const key = uniqueWithSuffix(relationType.key, used, suffix);
    const cloned = { ...relationType, key, label: `${relationType.label} import` };
    next.schema.relationTypes.push(cloned);
    used.add(key);
    existing.set(key, cloned);
    keyMap.set(relationType.key, key);
  }

  return keyMap;
};

const appendEntities = (
  next: SyncrelState,
  incomingEntities: SyncrelEntity[],
  fieldKeyMap: Map<string, string>,
  suffix: string,
  now: number,
): Map<string, string> => {
  const idMap = new Map<string, string>();
  const used = new Set(next.entities.map((entity) => entity.id));

  for (const entity of incomingEntities) {
    const id = uniqueWithSuffix(entity.id, used, suffix);
    used.add(id);
    idMap.set(entity.id, id);
    next.entities.push({
      ...entity,
      id,
      fields: remapFields(entity.fields, fieldKeyMap),
      polls: entity.polls?.map((poll) => ({
        ...poll,
        options: poll.options.map((option) => ({ ...option })),
        votes: poll.votes.map((vote) => ({ ...vote })),
      })),
      position: {
        x: entity.position.x + 48,
        y: entity.position.y + 48,
      },
      updatedAt: now,
    });
  }

  return idMap;
};

const appendRelations = (
  next: SyncrelState,
  incomingRelations: SyncrelRelation[],
  entityIdMap: Map<string, string>,
  relationTypeKeyMap: Map<string, string>,
  suffix: string,
): void => {
  const entityIds = new Set(next.entities.map((entity) => entity.id));
  const used = new Set(next.relations.map((relation) => relation.id));

  for (const relation of incomingRelations) {
    const fromId = entityIdMap.get(relation.fromId) ?? relation.fromId;
    const toId = entityIdMap.get(relation.toId) ?? relation.toId;
    if (!entityIds.has(fromId) || !entityIds.has(toId)) continue;
    const type = relationTypeKeyMap.get(relation.type) ?? relation.type;
    const id = uniqueWithSuffix(`rel-${fromId}-${toId}-${type}`, used, suffix);
    used.add(id);
    next.relations.push({
      ...relation,
      id,
      fromId,
      toId,
      type,
    });
  }
};

const appendChat = (
  next: SyncrelState,
  incomingMessages: SyncrelChatMessage[],
  suffix: string,
): void => {
  const used = new Set((next.chat ?? []).map((message) => message.id));
  const messages = incomingMessages.map((message) => {
    const id = uniqueWithSuffix(message.id, used, suffix);
    used.add(id);
    return { ...message, id };
  });
  next.chat = [...(next.chat ?? []), ...messages];
};

const mergedFrameworks = (current: SyncrelState, incoming: SyncrelState): FrameworkId[] => {
  const ids = [
    ...(current.workspace.enabledFrameworks ?? []),
    ...(current.workspace.activeFramework ? [current.workspace.activeFramework] : []),
    ...(incoming.workspace.enabledFrameworks ?? []),
    ...(incoming.workspace.activeFramework ? [incoming.workspace.activeFramework] : []),
  ];
  return Array.from(new Set(ids));
};

const cloneSchemaField = (field: SchemaField): SchemaField => ({
  ...field,
  options: field.options ? [...field.options] : undefined,
});

const remapFields = (
  fields: Record<string, unknown>,
  fieldKeyMap: Map<string, string>,
): Record<string, unknown> => {
  const next: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(fields)) {
    next[fieldKeyMap.get(key) ?? key] = value;
  }
  return next;
};

const schemaFieldsMatch = (left: SchemaField, right: SchemaField): boolean => (
  left.label === right.label &&
  left.type === right.type &&
  Boolean(left.readonly) === Boolean(right.readonly) &&
  Boolean(left.required) === Boolean(right.required) &&
  JSON.stringify(left.options ?? []) === JSON.stringify(right.options ?? [])
);

const relationTypesMatch = (left: RelationType, right: RelationType): boolean => (
  left.label === right.label && left.inverseLabel === right.inverseLabel
);

const uniqueWithSuffix = (seed: string, used: Set<string>, suffix: string): string => {
  if (!used.has(seed)) return seed;
  const base = `${seed}-${suffix}`;
  if (!used.has(base)) return base;
  let index = 2;
  while (used.has(`${base}-${index}`)) index += 1;
  return `${base}-${index}`;
};

const roomSlug = (value: string): string => {
  const slug = value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "");
  return slug || "workspace";
};

const fitRoomName = (value: string, reserve = 0): string => {
  const limit = Math.max(1, 64 - reserve);
  const clipped = value.slice(0, limit).replace(/-+$/g, "");
  return clipped || "workspace";
};
